import { formOptions } from '@tanstack/react-form';
import { extractLiquidVars } from '@tronche2tek/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  CardPhase,
  CardPreflightRecap,
  OutputFormat,
  PreflightInput,
} from '../../../api/cards';
import type { CardTemplateDto } from '../../../api/cards';
import {
  cancelRenderBatch,
  commitCards,
  discardPreflight,
  getCardTemplateSvgs,
  getPreflightRecap,
  postPreflight,
} from '../../../api/cards';
import type { SseHandle } from '../../../api/cards-sse';
import { openCardStream } from '../../../api/cards-sse';
import { useAppForm } from '../../../form-hook';
import type { CardProgressData, CardResult } from '../../../types';
import type { CardsSearch, CardsSearchPatch } from '../cards-url-state';
import { initialCardGenFields } from './card-batch';

/**
 * The form's shape, for `withForm` only.
 *
 * A `withForm` component receives the form itself rather than its values, and
 * it has to be told which form: spreading this into the call is what types
 * `form` inside `render`. Without it every field access there is `unknown`.
 *
 * The values are never used — the real defaults come from the URL, in
 * {@link useCardGenForm} — so this carries the type and nothing else.
 */
export const cardGenFormOpts = formOptions({
  defaultValues: initialCardGenFields(),
});

/**
 * The generator's form. Wrapped so the hooks and steps can name its exact type
 * — `setFieldValue` is typed against the field paths inferred from the initial
 * values, so a looser signature will not do.
 */
export function useCardGenForm(
  search: CardsSearch | undefined,
  onSubmit: () => void,
) {
  return useAppForm({
    defaultValues: initialCardGenFields(search),
    onSubmit: () => onSubmit(),
  });
}

export type CardGenForm = ReturnType<typeof useCardGenForm>;

/**
 * The template chosen for a batch, and the Liquid vars and stored defaults its
 * SVGs turn out to carry. None of that is typed by the operator, so it sits
 * beside the form rather than in it.
 *
 * The saved template is staged as a File like a custom upload, so everything
 * downstream — the pre-flight request, the CSV column hints — has one shape to
 * deal with.
 */
export function useCardTemplatePick({
  form,
  search,
  savedTemplates,
  onSearchPatch,
}: {
  form: CardGenForm;
  search?: CardsSearch;
  savedTemplates: CardTemplateDto[];
  onSearchPatch?: (patch: CardsSearchPatch) => void;
}) {
  const [frontVars, setFrontVars] = useState<string[]>([]);
  const [backVars, setBackVars] = useState<string[]>([]);
  const [defaults, setDefaults] = useState<Record<string, string>>({});

  const clear = useCallback(() => {
    form.setFieldValue('templateId', '');
    form.setFieldValue('templateFile', null);
    form.setFieldValue('values', {});
    // The sort field is one of the template's own variables, so it means
    // nothing once the template is gone — and carrying it over to the next
    // template would post a field that one does not have (a 400 at pre-flight).
    form.setFieldValue('sortBy', '');
    setFrontVars([]);
    setBackVars([]);
    setDefaults({});
  }, [form]);

  // Load a saved template: fetch its SVGs and extract front/back Liquid vars +
  // defaults. Done in the handler (a user action), so no effect is needed.
  const selectSaved = useCallback(
    async (templateId: string) => {
      clear();
      form.setFieldValue('templateId', templateId);
      if (!templateId) {
        return;
      }
      try {
        const {
          frontSvg,
          backSvg,
          name,
          defaults: stored,
        } = await getCardTemplateSvgs(templateId);
        const blob = new Blob([frontSvg], { type: 'image/svg+xml' });
        form.setFieldValue(
          'templateFile',
          new File([blob], `${name}.svg`, { type: 'image/svg+xml' }),
        );
        setFrontVars([...extractLiquidVars(frontSvg)]);
        setDefaults(stored);
        if (backSvg) {
          setBackVars([...extractLiquidVars(backSvg)]);
        } else {
          // No verso on this template — a lingering single-side choice
          // (state or shared URL) no longer applies.
          form.setFieldValue('sides', 'front');
          onSearchPatch?.({ sides: undefined });
        }
      } catch {
        /* noop — the picker simply stays empty */
      }
    },
    [clear, onSearchPatch, form],
  );

  // Custom SVG upload: read it and extract its Liquid vars.
  const pickFile = useCallback(
    async (f: File) => {
      clear();
      form.setFieldValue('sides', 'front'); // a custom upload is a single (front) SVG
      onSearchPatch?.({ sides: undefined });
      form.setFieldValue('templateFile', f);
      setFrontVars([...extractLiquidVars(await f.text())]);
    },
    [clear, onSearchPatch, form],
  );

  // Restore a URL-shared saved template once the template list is loaded, then
  // re-apply the shared form values (loading a template clears them). Runs at
  // most once; an id that no longer exists is simply never applied.
  const urlAppliedRef = useRef(false);
  useEffect(() => {
    if (urlAppliedRef.current) {
      return;
    }
    const wanted = search?.template;
    if (!wanted || !savedTemplates.some((tpl) => tpl.id === wanted)) {
      return;
    }
    urlAppliedRef.current = true;
    const values = search.values;
    const sortBy = search.sortBy;
    void selectSaved(wanted).then(() => {
      if (values && Object.keys(values).length > 0) {
        form.setFieldValue('values', values);
      }
      // Re-applied for the same reason as the values: loading the template
      // cleared it. A field this template turns out not to carry is dropped
      // downstream by `effectiveSortBy`, not here — the vars are still being
      // extracted at this point.
      if (sortBy) {
        form.setFieldValue('sortBy', sortBy);
      }
    });
  }, [
    savedTemplates,
    search?.template,
    search?.values,
    search?.sortBy,
    selectSaved,
    form,
  ]);

  // One stable object per change: consumers hold this in memo/callback deps
  // (deriveBatch in the steps), so a fresh literal here would void them all.
  return useMemo(
    () => ({ frontVars, backVars, defaults, clear, selectSaved, pickFile }),
    [frontVars, backVars, defaults, clear, selectSaved, pickFile],
  );
}

/** Progress arriving within this window after a throttle signal is just the
 * workers' in-flight stragglers finishing — it must not clear the banner. */
const THROTTLE_CLEAR_GRACE_MS = 10_000;

/** Non-null while an upstream rate limit has the batch paused. */
export interface ThrottleState {
  service: string;
  resumeAt: number;
}

/**
 * A card batch's whole life: pre-flight, recap, commit, render, and the SSE
 * stream all four publish to. None of it is form state — it describes what the
 * server is doing — so it lives here rather than in the generator's form.
 *
 * The batch id also lives in the URL, so a refresh mid-flow lands back where it
 * left off; `onSearchPatch` is how that is kept in step.
 */
export function useCardBatchRun({
  search,
  onSearchPatch,
}: {
  search?: CardsSearch;
  onSearchPatch?: (patch: CardsSearchPatch) => void;
}) {
  const [phase, setPhase] = useState<CardPhase>('idle');
  const [progress, setProgress] = useState<CardProgressData>({
    done: 0,
    total: 0,
  });
  const [results, setResults] = useState<CardResult[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [resolveBatchId, setResolveBatchId] = useState<string | null>(null);
  const [recap, setRecap] = useState<CardPreflightRecap | null>(null);
  const [deduplicated, setDeduplicated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [throttled, setThrottled] = useState<ThrottleState | null>(null);
  // Every card is rendered; the server is assembling the zip/pdf — minutes of
  // otherwise-silent work on a large batch, shown instead of a stalled bar.
  const [finalizing, setFinalizing] = useState(false);
  const esRef = useRef<SseHandle | null>(null);

  // When a queue pauses, the workers' already in-flight jobs keep completing
  // for a few seconds — their progress events must not wipe the banner the
  // pause just raised (it flashed for milliseconds and looked absent). Only
  // sustained progress past the grace means the throttle actually ended.
  const throttledAtRef = useRef(0);

  const fail = useCallback((message: string) => {
    setError(message);
    setPhase('error');
  }, []);

  const closeStream = () => {
    esRef.current?.close();
    esRef.current = null;
  };

  // Subscribe to the batch progress SSE stream. Both phases publish to the same
  // channel; `onDone` decides what happens when the batch finishes (show the
  // recap for resolve, mark done for render).
  const openStream = useCallback(
    async (id: string, onDone: () => void) => {
      await openCardStream({
        path: `/cards/stream/${id}`,
        esRef,
        onProgress: (data) => {
          if (Date.now() - throttledAtRef.current > THROTTLE_CLEAR_GRACE_MS) {
            setThrottled(null);
          }
          setProgress({
            done: data.done,
            total: data.total,
            current: data.current,
          });
          const { result } = data;
          if (result) {
            setResults((prev) => [...prev, result]);
          }
        },
        onThrottled: (data) => {
          throttledAtRef.current = Date.now();
          setThrottled(data);
        },
        onFinalizing: () => setFinalizing(true),
        onDone,
        onError: fail,
      });
    },
    [fail],
  );

  // Resolve finished → pull the recap and let the user cancel or continue.
  const loadRecap = useCallback(
    async (id: string) => {
      try {
        setRecap(await getPreflightRecap(id));
        setPhase('recap');
      } catch (err) {
        fail(err instanceof Error ? err.message : String(err));
      }
    },
    [fail],
  );

  const runResolve = useCallback(
    async (id: string) => {
      setPhase('preflighting');
      await openStream(id, () => {
        void loadRecap(id);
      });
    },
    [openStream, loadRecap],
  );

  const runRender = useCallback(
    async (id: string) => {
      setJobId(id);
      setResults([]);
      setProgress({ done: 0, total: 0 });
      setThrottled(null);
      setFinalizing(false);
      setPhase('processing');
      await openStream(id, () => setPhase('done'));
    },
    [openStream],
  );

  /**
   * Re-enter a pre-flight batch by id: straight to the recap when it finished
   * resolving (the recap is persisted server-side for 24h), back onto the live
   * stream otherwise. Entry points: the `?resume=` URL param (a refresh
   * mid-flow) and the pending-review banner (coming back later).
   */
  const resumedRef = useRef<string | null>(null);
  const resumeBatch = useCallback(
    async (id: string) => {
      resumedRef.current = id; // pre-empt the URL effect — no double entry
      setResolveBatchId(id);
      setResults([]);
      setError(null);
      onSearchPatch?.({ resume: id, job: undefined });
      try {
        setRecap(await getPreflightRecap(id));
        setPhase('recap');
      } catch {
        // No recap yet: still resolving — re-attach to the stream. (An
        // expired/foreign id 404s there and surfaces as a stream error.)
        await runResolve(id);
      }
    },
    [onSearchPatch, runResolve],
  );

  /**
   * Re-attach to a committed render batch: live stream while it generates,
   * replayed results + download once done. Entry points: the `?job=` URL param
   * (a refresh on the run & download step) and the jobs tracker.
   */
  const resumeRender = useCallback(
    async (id: string) => {
      resumedRef.current = id; // pre-empt the URL effect — no double entry
      setError(null);
      setDeduplicated(false);
      onSearchPatch?.({ job: id, resume: undefined });
      await runRender(id);
    },
    [onSearchPatch, runRender],
  );

  // Auto-resume the URL's batch (once per id — a refresh mid-flow lands back
  // where it left off, and a jobs-tracker click while already mounted swaps to
  // the clicked batch). A render job wins over a pre-flight: it is the later
  // step, and commit clears `resume` anyway.
  useEffect(() => {
    const id = search?.job ?? search?.resume;
    if (!id || resumedRef.current === id) {
      return;
    }
    resumedRef.current = id;
    if (search?.job) {
      void resumeRender(search.job);
    } else {
      void resumeBatch(id);
    }
  }, [search?.job, search?.resume, resumeBatch, resumeRender]);

  /** Phase 1 — post the request, then stream the resolve progress. */
  const startPreflight = useCallback(
    async (input: PreflightInput) => {
      setPhase('uploading');
      setResults([]);
      setRecap(null);
      setError(null);

      let id: string;
      try {
        id = await postPreflight(input);
      } catch (err) {
        fail(err instanceof Error ? err.message : String(err));
        return;
      }

      setResolveBatchId(id);
      // Persist the batch in the URL so a refresh mid-flow resumes it (and drop
      // any previous render job — this is a new flow).
      resumedRef.current = id;
      onSearchPatch?.({ resume: id, job: undefined });
      await runResolve(id);
    },
    [fail, onSearchPatch, runResolve],
  );

  /** Phase 2 — commit. Render only the validated recipients. */
  const commit = useCallback(
    async (outputFormat: OutputFormat) => {
      if (!resolveBatchId) {
        return;
      }
      setPhase('committing');
      setError(null);
      let id: string;
      try {
        const res = await commitCards(resolveBatchId, outputFormat);
        id = res.jobId;
        setDeduplicated(res.deduplicated);
      } catch (err) {
        fail(err instanceof Error ? err.message : String(err));
        return;
      }
      // The render job supersedes the pre-flight in the URL: a refresh from
      // here on re-attaches to the run & download step, not the recap.
      resumedRef.current = id;
      onSearchPatch?.({ resume: undefined, job: id });
      await runRender(id);
    },
    [fail, resolveBatchId, runRender, onSearchPatch],
  );

  /**
   * Fire-and-forget discard of the current pre-flight (recap adjust/cancel):
   * the UI moves on immediately; a failed discard only leaves the batch to its
   * 24h TTL.
   */
  const discardCurrent = useCallback(() => {
    if (!resolveBatchId) {
      return;
    }
    void discardPreflight(resolveBatchId).catch(() => undefined);
  }, [resolveBatchId]);

  /**
   * Back to the filled form: clear only the batch lifecycle (progress,
   * results, ids), keeping the template/recipients/options as they were.
   */
  const newBatch = useCallback(() => {
    closeStream();
    setPhase('idle');
    setProgress({ done: 0, total: 0 });
    setResults([]);
    setFinalizing(false);
    setJobId(null);
    setResolveBatchId(null);
    setRecap(null);
    setDeduplicated(false);
    setError(null);
    onSearchPatch?.({ resume: undefined, job: undefined });
  }, [onSearchPatch]);

  /**
   * Cancel mid-run (resolving or rendering): close our stream first so the
   * server's terminal event doesn't flip us to the error phase, then cancel
   * server-side and return to the filled form. Fire-and-forget — a failed
   * cancel (e.g. the batch just finished) only leaves it to its TTL.
   */
  const cancelRun = useCallback(() => {
    closeStream();
    if (phase === 'processing' && jobId) {
      void cancelRenderBatch(jobId).catch(() => undefined);
    } else if (resolveBatchId) {
      void discardPreflight(resolveBatchId).catch(() => undefined);
    }
    newBatch();
  }, [phase, jobId, resolveBatchId, newBatch]);

  // Same identity rule as useCardTemplatePick above.
  return useMemo(
    () => ({
      phase,
      progress,
      results,
      jobId,
      resolveBatchId,
      recap,
      deduplicated,
      error,
      throttled,
      finalizing,
      startPreflight,
      commit,
      resumeBatch,
      discardCurrent,
      newBatch,
      cancelRun,
    }),
    [
      phase,
      progress,
      results,
      jobId,
      resolveBatchId,
      recap,
      deduplicated,
      error,
      throttled,
      finalizing,
      startPreflight,
      commit,
      resumeBatch,
      discardCurrent,
      newBatch,
      cancelRun,
    ],
  );
}
